import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Edit } from '@material-ui/icons';
import { format } from 'date-fns';
import { stringify } from 'querystring';

import { UnidataTable, Modal, Table } from '~/components';
import { t, number } from '~/helpers';
import { formatISO } from '~/helpers/dateTime';
import { getToastErrors } from '~/helpers/errorsRequest';
import api from '~/services/api';
import {
  recordFiltering,
  findData,
  deleteData,
} from '~/store/modules/EdicaoOperacao/Recebimento/actions';

import { VirtualTanks } from './components/VirtualTanks';
import * as S from './styles';

export default function Recebimento() {
  const [queryParams, setQueryParams] = useState({});
  const [enableEditing, setEnableEditing] = useState(false);
  const [virtualTanksLoading, setVirtualTanksLoading] = useState(false);
  const [virtualTanks, setVirtualTanks] = useState([]);

  const dispatch = useDispatch();
  const redux = useSelector((state) => state.recebimento);
  const receivementRef = useRef(null);

  const headerReceivement = useMemo(
    () => [
      {
        id: 'datahorainiciodescarganf',
        label: t('s.operacao.recebimento.datahorainiciodescarga'),
        formatted: (date) => formatISO({ date }),
        filter: {
          type: 'initialdatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 00:00:00'),
        },
      },
      {
        id: 'datahorafimdescarganf',
        label: t('s.operacao.recebimento.datahorafimdescarga'),
        formatted: (date) => formatISO({ date }),
        filter: {
          type: 'finaldatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 23:59:59'),
        },
      },
      {
        id: 'codigoposto',
        label: t('s.configuracao.terminal.idposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigonomeposto',
        label: t('s.menu.configuracao.posto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nomeproduto',
        label: t('s.menu.configuracao.produto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'cnpjdistribuidoradescarganf',
        label: t('s.operacao.recebimentonf.cnpjdistribuidoradescarganf'),
      },
      {
        id: 'cnpjtransportadoradescarganf',
        label: t('s.operacao.recebimentonf.cnpjtransportadoradescarganf'),
      },
      {
        id: 'nomeusuario',
        label: t('s.menu.cadastro.usuario'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'datahoradescarganf',
        label: t('s.operacao.recebimentonf.dataemissaonfdescarganf'),
        formatted: (date) => formatISO({ date, output: 'dd/MM/yyyy' }),
      },
      {
        id: 'numeropedidodescarganf',
        label: t('s.relatorio.descarga.pedido'),
        filter: {
          type: 'number',
        },
      },
      {
        id: 'nfdescarganf',
        label: t('s.relatorio.geral.notafiscal'),
        filter: {
          type: 'number',
        },
      },
      {
        id: 'placactdescarganf',
        label: t('s.menu.cadastro.placact'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'motoristadescarganf',
        label: t('s.menu.cadastro.motorista'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'volumenfdescarganf',
        label: t('s.operacao.recebimento.volumenfdescarga'),
        formatted: (value) => number(value, 2),
      },
      {
        id: 'precounitariodescarganf',
        label: t('s.operacao.abastecimento.precounitariomovimentacao'),
        formatted: (value) => number(value, 2),
      },
    ],
    []
  );

  const headerVirtualTanks = useMemo(
    () => [
      {
        id: 'idtanquevirtual',
        label: t('s.menu.configuracao.tanquevirtual'),
      },
      {
        id: 'codigotanquefisico',
        label: t('s.menu.configuracao.tanquefisico'),
      },
      {
        id: 'alturainicialdescarganftanque',
        label: t('s.geral.alturainicial'),
        format: (value) => number(value, 2),
      },
      {
        id: 'volumeinicialdescarganftanque',
        label: t('s.operacao.turno.volinicontabil'),
        format: (value) => number(value, 2),
      },
      {
        id: 'alturafinaldescarganftanque',
        label: t('s.geral.alturafinal'),
        format: (value) => number(value, 2),
      },
      {
        id: 'volumefinaldescarganftanque',
        label: t('s.geral.volumefinal'),
        format: (value) => number(value, 2),
      },
    ],
    []
  );

  const buttons = useMemo(
    () => [
      {
        title: t('s.geral.botaoatualizar'),
        icon: <Edit />,
        action: () =>
          receivementRef.current.handleOpenModal({
            params: queryParams,
          }),
      },
    ],
    [queryParams]
  );

  const isSearchForVirtualTanks = useCallback(async () => {
    try {
      setVirtualTanksLoading(true);

      const { iddescarganf, idposto } = queryParams;

      await api
        .get(`descargasnfs/${iddescarganf}/${idposto}`)
        .then(({ data }) => setVirtualTanks(data.tanques ?? []));
    } catch (err) {
      getToastErrors(err);
    } finally {
      setVirtualTanksLoading(false);
    }
  }, [queryParams]);

  const handleSetReceivementIsActive = useCallback((data) => {
    setQueryParams(data);
  }, []);

  const isRefreshTankGrid = useCallback(() => {
    dispatch(recordFiltering(redux.params));
    isSearchForVirtualTanks();
  }, [redux.params, isSearchForVirtualTanks]);

  useEffect(() => {
    if (typeof queryParams?.idproduto !== 'undefined') {
      isSearchForVirtualTanks();
    }
  }, [queryParams]);

  return (
    <S.Wrapper>
      <UnidataTable
        fixed
        redux={redux}
        header={headerReceivement}
        read={recordFiltering}
        find={findData}
        remove={deleteData}
        onRow={handleSetReceivementIsActive}
        actions={buttons}
        update={false}
        keys={['iddescarganf', 'idposto']}
        detach="nomeposto"
        maxHeight="40rem"
      />

      <Table
        cells={headerVirtualTanks}
        data={virtualTanks}
        loading={virtualTanksLoading}
        paginate={100}
        showPagination={false}
        unique="rownum"
        size="small"
      />

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        form
        title={t('s.menu.operacao.recebimento')}
        ref={receivementRef}
        onClose={isRefreshTankGrid}
        component={VirtualTanks}
      />
    </S.Wrapper>
  );
}
